/* @ts-ignore */
// @ts-nocheck

import { FC, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  selectDisplayValueMode,
  selectForecastItems,
  selectPlannedItems,
  selectShipmentItems,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import {
  DisplayValueMode,
  Product,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { formatCurrency } from '@/core/utils/formatCurrency.ts'
import {
  ArrowIconWrapper,
  Statistic,
  Wrapper,
} from './ShippedMonthCell.styles.ts'
import ArrowUpIcon from '@/core/assets/icons/arrow-up.svg'
import { getPlannedShippedForecastForMonth } from '@/core/utils/getPlannedShippedForecastForMonth.ts'
import { formatNumber } from '@/core/utils/formatNumber.ts'
import { bindPopper, bindTrigger } from 'material-ui-popup-state'
import { usePopupState } from 'material-ui-popup-state/hooks'
import { CustomFade } from '@/ui/components/CustomFade'
import { Box, Popper } from '@mui/material'
import { PlanHistory } from '@/ui/components/PlanHistory'
import { isCurrentMonth } from '@/core/utils/isCurrentMonth.ts'

interface Props {
  date: Date
  product: Product
}

const ShippedMonthCell: FC<Props> = ({ date, product }) => {
  const displayValueMode = useSelector(selectDisplayValueMode)
  const isCurrent = isCurrentMonth(date)

  const forecastItems = useSelector(selectForecastItems)
  const plannedItems = useSelector(selectPlannedItems)
  const shipmentItems = useSelector(selectShipmentItems)

  const shippedPlannedValues = useMemo(() => {
    const { plannedAmount, shippedAmount, shippedCost, plannedCost } =
      getPlannedShippedForecastForMonth(product, date, {
        forecast: forecastItems,
        planned: plannedItems,
        shipments: shipmentItems,
      })

    return {
      planned: plannedAmount,
      amount: shippedAmount,
      cost: shippedCost,
      plannedCost,
    }
  }, [date, forecastItems, plannedItems, product, shipmentItems])

  const plannedAmountValue = shippedPlannedValues.planned
  const shippedAmountValue = shippedPlannedValues.amount

  const numericValue = isCurrent
    ? displayValueMode === DisplayValueMode.amount
      ? shippedPlannedValues.planned
      : shippedPlannedValues.plannedCost
    : displayValueMode === DisplayValueMode.amount
      ? shippedPlannedValues.amount
      : shippedPlannedValues.cost

  const displayValue =
    displayValueMode === DisplayValueMode.cost
      ? numericValue
        ? formatCurrency(numericValue, { maximumFractionDigits: 0 })
        : '—'
      : numericValue
        ? formatNumber(numericValue)
        : '—'

  const canShowStatistic =
    Boolean(plannedAmountValue) && Boolean(shippedAmountValue)
  const percentage = canShowStatistic
    ? isCurrent
      ? Math.min(
          100,
          Math.round((shippedAmountValue! / plannedAmountValue!) * 100),
        )
      : (
          (100 * (shippedAmountValue! - plannedAmountValue!)) /
          plannedAmountValue!
        ).toFixed(0)
    : null

  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'planned-history-popover',
  })

  const popupKey = useMemo(() => {
    const productKey =
      (product as any)?.id ??
      (product as any)?.productId ??
      (product as any)?.sku ??
      'unknown'
    return `${productKey}-${date.toISOString()}`
  }, [product, date])

  useEffect(() => {
    const handleOtherOpen = (event: Event) => {
      const openedId = (event as CustomEvent).detail
      if (openedId !== popupKey) {
        popupState.close()
      }
    }
    window.addEventListener(
      'plan-history-open',
      handleOtherOpen as EventListener,
    )
    return () => {
      window.removeEventListener(
        'plan-history-open',
        handleOtherOpen as EventListener,
      )
    }
  }, [popupKey, popupState])

  const triggerProps = bindTrigger(popupState)

  return (
    <>
      <Wrapper
        {...triggerProps}
        onClick={(e) => {
          window.dispatchEvent(
            new CustomEvent('plan-history-open', { detail: popupKey }),
          )
          triggerProps.onClick?.(e)
        }}
        onMouseLeave={popupState.close}
      >
        {canShowStatistic && percentage && (
          <Statistic
            className={
              isCurrent
                ? Number(percentage) >= 100
                  ? 'positive'
                  : 'negative'
                : shippedAmountValue! > plannedAmountValue!
                  ? 'positive'
                  : 'negative'
            }
          >
            {!isCurrent && (
              <ArrowIconWrapper>
                <ArrowUpIcon />
              </ArrowIconWrapper>
            )}
            {percentage}%
          </Statistic>
        )}

        {displayValue}
      </Wrapper>

      <Popper {...bindPopper(popupState)} transition placement="bottom-start">
        {({ TransitionProps }) => (
          <CustomFade {...TransitionProps} timeout={250} delay={0}>
            <Box py="2px" onMouseLeave={popupState.close}>
              <PlanHistory date={date} product={product} color="primary" />
            </Box>
          </CustomFade>
        )}
      </Popper>
    </>
  )
}

export default ShippedMonthCell
